import { useMemo } from 'react'

import Badge from '../Badge'
import IconButton from '../IconButton'
import RelativeTime from '../RelativeTime'
import { TableColumn } from '../Table'

import { getDomainFromUrl, getPlatformVariant, getStatusVariant, getStatusDisplay } from './helpers'
import { SearchQuery } from './types'

export function useSearchQueriesColumns(
  handleViewFailedJobsWrapper: (queryId: string, queryName: string) => Promise<void>,
  handleDeleteQueryWrapper: (id: string) => Promise<void>
): TableColumn<SearchQuery>[] {
  return useMemo(() => [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      required: true, // Name column is required if no ID column exists
      render: (_value, row) => (
        <div>
          <div className="text-sm font-medium text-text-primary">{row.name}</div>
          <a
            href={row.search_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-brand-primary hover:text-brand-primary-strong hover:underline"
            title={row.search_url}
            onClick={(e) => e.stopPropagation()}
          >
            {getDomainFromUrl(row.search_url)}
          </a>
        </div>
      ),
    },
    {
      key: 'last_scraped_at',
      label: 'Last Scraped',
      sortable: true,
      render: (value) => (
        <RelativeTime date={value} format="full" className="text-sm text-text-secondary" />
      ),
    },
    {
      key: 'platform',
      label: 'Platform',
      render: (value) => (
        <Badge variant={getPlatformVariant(value)}>
          {value === 'linkedin' ? 'LinkedIn' : value.charAt(0).toUpperCase() + value.slice(1)}
        </Badge>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <Badge variant={getStatusVariant(value)}>
          {getStatusDisplay(value)}
        </Badge>
      ),
    },
    {
      key: 'total_jobs_found',
      label: 'Total Jobs',
      sortable: true,
      render: (_value, row) => (
        <span className="text-sm text-text-primary">
          {row.total_jobs_found > 0 ? (
            <span className="font-medium">
              {row.total_jobs_found}
              {row.scraped_jobs_count >= row.total_jobs_found ? '+' : ''}
            </span>
          ) : (
            <span className="text-text-muted">—</span>
          )}
        </span>
      ),
    },
    {
      key: 'scraped_jobs_count',
      label: 'Scraped',
      sortable: true,
      render: (_value, row) => (
        <div className="flex flex-col items-start">
          <span className="leading-tight text-sm text-text-primary">{row.scraped_jobs_count}</span>
          {row.failed_jobs_count && row.failed_jobs_count > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleViewFailedJobsWrapper(row.id, row.name)
              }}
              className="text-xs text-danger hover:text-red-800 hover:underline leading-tight"
            >
              {row.failed_jobs_count} failed (view)
            </button>
          )}
        </div>
      ),
    },
    {
      key: 'progress',
      label: 'Progress',
      render: (_value, row) => {
        if (row.total_jobs_found > 0) {
          if (row.scraped_jobs_count <= row.total_jobs_found) {
            const percentage = Math.round((row.scraped_jobs_count / row.total_jobs_found) * 100)
            return <span className="font-medium text-sm text-text-primary">{percentage}%</span>
          } else {
            return <span className="font-medium text-sm text-text-primary">99%</span>
          }
        } else if (row.scraped_jobs_count > 0) {
          return <span className="font-medium text-sm text-text-muted">—</span>
        } else {
          return <span className="text-sm text-text-muted">—</span>
        }
      },
    },
    {
      key: 'relevance_verdict_summary',
      label: 'Verdict',
      sortable: true,
      render: (_value, row) => {
        if (row.relevance_verdict_summary) {
          const { fit, no_fit } = row.relevance_verdict_summary
          const total = fit + no_fit
          if (total > 0) {
            return (
              <div className="flex flex-col items-start gap-1">
                <span className="text-sm text-text-primary">
                  <span
                    role="button"
                    tabIndex={0}
                    className="font-medium text-brand-primary cursor-pointer hover:text-brand-primary-strong hover:underline"
                    onClick={(e) => {
                      e.stopPropagation()
                      window.location.href = `/?searchQueryId=${row.id}&relevanceFilter=relevant`
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        e.stopPropagation()
                        window.location.href = `/?searchQueryId=${row.id}&relevanceFilter=relevant`
                      }
                    }}
                    title="Click to view Relevant jobs"
                  >
                    {fit} Fit
                  </span>
                  {no_fit > 0 && (
                    <>
                      <span className="text-text-muted"> / </span>
                      <span
                        role="button"
                        tabIndex={0}
                        className="font-medium text-danger cursor-pointer hover:text-danger-strong hover:underline"
                        onClick={(e) => {
                          e.stopPropagation()
                          window.location.href = `/?searchQueryId=${row.id}&relevanceFilter=non-relevant`
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            e.stopPropagation()
                            window.location.href = `/?searchQueryId=${row.id}&relevanceFilter=non-relevant`
                          }
                        }}
                        title="Click to view Non-Relevant jobs"
                      >
                        {no_fit} No Fit
                      </span>
                    </>
                  )}
                </span>
              </div>
            )
          }
        }
        return <span className="text-sm text-text-muted">—</span>
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      sticky: 'right',
      align: 'center',
      render: (_value, row) => (
        <div className="flex justify-center">
          <IconButton
            icon={
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            }
            onClick={(e) => {
              e.stopPropagation()
              handleDeleteQueryWrapper(row.id)
            }}
            variant="danger"
            title="Delete"
          />
        </div>
      ),
    },
  ], [handleViewFailedJobsWrapper, handleDeleteQueryWrapper])
}
